import { useId, type ReactNode } from "react";
import { ActionReplyForm } from "@/components/submissions/ActionReplyForm";
import {
  formatShortDate,
  formatShortDateTime,
  statusBadgeClass,
} from "@/lib/listFormat";
import {
  actionAnchor,
  actionEventLabel,
  actionKindLabel,
  actionStatusLabel,
  type ActionItem,
} from "@/lib/submissions/actionItems";

/**
 * One action Signal works for the broker (PMS-S872): its kind and where it
 * stands, in words; when it opened and last changed; what Signal needs from
 * the broker, set apart, with the broker's reply (BRK-S464), while it waits
 * on them; and the events Signal shares, oldest first. A quote's Actions tab
 * shows its own (BRK-S465); the submission's shows them all, each naming its
 * quote (BRK-S464).
 */
export function ActionItemCard({
  item,
  submissionId,
  onChanged,
  quote,
  focused = false,
}: {
  item: ActionItem;
  submissionId: string;
  /** Reads the actions again, once a reply lands. */
  onChanged: () => void | Promise<void>;
  /** A link to the item's quote, on the submission's tab. */
  quote?: ReactNode;
  /** The one a link named (`?action=`): set apart, and scrolled to. */
  focused?: boolean;
}) {
  const headingId = useId();
  const needId = useId();
  return (
    <article
      id={actionAnchor(item.id)}
      aria-labelledby={headingId}
      tabIndex={focused ? -1 : undefined}
      // A white card like the page's others (QuoteCard), never the page's
      // grey (BRK-S494).
      className={`flex min-w-0 scroll-mt-4 flex-col gap-3 rounded-lg border bg-brk-panel p-4 outline-none ${
        focused
          ? "border-brk-accent ring-2 ring-brk-accent/30"
          : "border-brk-border"
      }`}
    >
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h4 id={headingId} className="text-subheading text-brk-text">
          {actionKindLabel(item)}
        </h4>
        <span
          className={`inline-flex shrink-0 items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${statusBadgeClass(item.status)}`}
        >
          {actionStatusLabel(item.status)}
        </span>
      </div>
      {quote && <p className="text-brk-text">{quote}</p>}
      <p className="text-meta text-brk-dim">
        Opened {formatShortDate(item.openedAt)} · Updated{" "}
        {formatShortDate(item.updatedAt)}
      </p>
      {item.openRequest && (
        <div
          role="note"
          aria-labelledby={needId}
          className="flex flex-col gap-1 rounded-md border border-amber-300 bg-amber-50 p-3 dark:border-amber-400/40 dark:bg-amber-400/10"
        >
          <p id={needId} className="font-semibold text-brk-text">
            What Signal needs
          </p>
          <p className="whitespace-pre-line break-words text-brk-text">
            {item.openRequest.message}
          </p>
          <ActionReplyForm
            submissionId={submissionId}
            item={item}
            onChanged={onChanged}
          />
        </div>
      )}
      {item.events.length > 0 && (
        <ol className="flex flex-col gap-3 border-l border-brk-border pl-3">
          {item.events.map((event) => (
            <li key={event.id} className="flex min-w-0 flex-col gap-0.5">
              <p className="font-medium text-brk-text">
                {actionEventLabel(event.type)}
              </p>
              <p className="text-meta text-brk-dim">
                {event.author.name} · {formatShortDateTime(event.at)}
              </p>
              {event.body && (
                <p className="whitespace-pre-line break-words text-brk-text">
                  {event.body}
                </p>
              )}
              {event.documents.length > 0 && (
                <p className="break-words text-meta text-brk-dim">
                  Attached:{" "}
                  {event.documents.map((doc) => doc.filename).join(", ")}
                </p>
              )}
            </li>
          ))}
        </ol>
      )}
    </article>
  );
}
